import { useEffect, useId, useRef, useState } from 'react';
import { inputCls } from './styles';
type Props = {
  value: number | undefined;
  /** Krijgt alleen geldige getallen; `undefined` alleen bij een leeg veld én `allowEmpty`. */
  onChange: (value: number | undefined) => void;
  /** Bedragen: komma of punt, hoogstens twee decimalen ("12,50"). */
  decimals?: boolean;
  min?: number;
  max?: number;
  /** Leeg mag (bijv. "voorraad niet bijhouden"). Anders blijft de oude waarde staan met een melding. */
  allowEmpty?: boolean;
  /** Bericht bij een leeg veld als dat niet mag. */
  emptyMessage?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

/** Euro's en aantallen zoals je ze in Nederland typt: "12,50" en "€ 12,50" werken allebei. */
const parse = (raw: string, decimals: boolean): number | null => {
  const t = raw.replace(/[€\s]/g, '').replace(',', '.');
  if (t === '') return null;
  const ok = decimals ? /^\d+(\.\d{0,2})?$/.test(t) : /^\d+$/.test(t);
  return ok ? Number(t) : Number.NaN;
};
const format = (v: number | undefined, decimals: boolean) => {
  if (v === undefined || !Number.isFinite(v)) return '';
  return decimals && !Number.isInteger(v) ? v.toFixed(2).replace('.', ',') : String(v);
};

/**
 * Getalveld voor het beheer. Vervangt `type="number"`, dat een komma in veel browsers stil
 * weggooit: "12,50" werd dan € 0. Een ongeldige invoer wordt nooit opgeslagen; de laatste
 * goede waarde blijft staan en er verschijnt een melding onder het veld.
 */
export function NumberInput({
  value,
  onChange,
  decimals = false,
  min = 0,
  max,
  allowEmpty = false,
  emptyMessage = 'Vul een getal in.',
  disabled,
  placeholder,
  className = ''
}: Props) {
  const errorId = useId();
  const [text, setText] = useState(() => format(value, decimals));
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);

  // Waarde van buitenaf (ander product geopend, ongedaan maken): alleen overnemen als je niet aan het typen bent.
  useEffect(() => {
    if (!focused.current) {
      setText(format(value, decimals));
      setError(null);
    }
  }, [value, decimals]);
  const handle = (raw: string) => {
    setText(raw);
    const n = parse(raw, decimals);
    if (n === null) {
      if (allowEmpty) {
        setError(null);
        onChange(undefined);
      } else setError(emptyMessage);
      return;
    }
    if (Number.isNaN(n)) return setError(decimals ? 'Alleen cijfers en een komma, bijv. 12,50.' : 'Alleen hele getallen, bijv. 3.');
    if (n < min) return setError(`Minimaal ${format(min, decimals)}.`);
    if (max !== undefined && n > max) return setError(`Hoogstens ${format(max, decimals)}.`);
    setError(null);
    onChange(n);
  };
  return <>
      <input type="text" inputMode={decimals ? 'decimal' : 'numeric'} value={text} disabled={disabled} placeholder={placeholder} onFocus={() => {
      focused.current = true;
    }} onBlur={() => {
      focused.current = false;
      // Netjes terugzetten ("12,5" → "12,50"); bij een fout laten staan wat ze typte.
      if (!error) setText(format(value, decimals));
    }} onChange={e => handle(e.target.value)} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} className={`${inputCls} tabular-nums ${className}`} data-id="element-5104" data-mp="15qqzpcp8xf14" />
      {error && <span id={errorId} role="alert" className="mt-1 block text-[12px] text-clay" data-id="element-5105" data-mp="300pdff2lh28i">
          {error}
        </span>}
    </>;
}